/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import IcomoonReact from 'icomoon-react';
import { TransitionGlobal } from '../../../shared/GlobalStyles';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import Text from '../../../shared/components/Text/Text';
import Spinner from '../../../shared/components/Spinner/Spinner';
import { editThumbnail } from '../ducks/calendarActions';

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 500px;
  border-radius: 8px;
  padding: 24px;
  gap: 16px;
`;

const DragHereContainer = styled('div')`
  display: flex;
  max-height: 321px;
  min-height: 207px;
  border: ${(props) => (props.image ? '1px solid #EEEEEE' : '1px dashed #77A7FF')};
  border-radius: 4px;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 8px;
  background-color: ${(props) => (props.isDragActive ? 'white' : '#FCFCFC')};
  opacity: ${(props) => (props.isDragActive ? 0.7 : 1)};
  cursor: pointer;
  transition: ${TransitionGlobal};
  ${(props) => !props.image && (':hover {opacity: 0.7;}')}
`;

const LoadingContainer = styled('div')`
  display: flex;
  max-height: 400px;
  min-height: 207px;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 8px;
  background-color: #FCFCFC;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SaveButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  background: ${(props) => props.backgroundColor};
  color: white;
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  user-select: none;
  border: 1px solid ${(props) => props.backgroundColor};
   ${(props) => !props.disabled && 'cursor: pointer;'}
  transition: ${TransitionGlobal};
  ${(props) => !props.disabled && ':hover { opacity: 0.7; }'}
`;

const CancelButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  color: #444444;
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  user-select: none;
  border: 2px solid #444444;
  cursor: pointer;
  transition: ${TransitionGlobal};
  :hover { 
    opacity: 0.7; 
  }
`;

const AddThumbnailModalContent = ({ closeModal }) => {
  const dispatch = useDispatch();
  const [image, setImage] = useState(null);
  const [imageFileIsLoading, setImagFileIsLoading] = useState(false);
  const [fileForThumbnail, setFileForThumbnail] = useState(null);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const uploadedMedia = useSelector((state) => state.calendar.data.uploadedMedia);
  const uploadedMediaId = uploadedMedia?.id;
  const isThumbnailLoading = useSelector((state) => state.calendar.ui.isThumbnailLoading);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setImagFileIsLoading(true);
    if (file) {
      setTimeout(() => {
        setImagFileIsLoading(false);
        setImage(URL.createObjectURL(file));
        setFileForThumbnail(file);
      }, 800);
    } else {
      setImagFileIsLoading(false);
      toast.error('Please drop a valid  .jpg or .jpeg file.', { theme: 'light' });
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/jpg',
    maxFiles: 1,
  });

  const handleUpdateThumbnail = () => {
    dispatch(editThumbnail(accountCode, fileForThumbnail, 'file', uploadedMediaId, {
      onSuccess: () => {
        closeModal();
      },
      onError: () => {
        setImage(null);
        toast.error('Opps! Image could not be uploaded. Try again or use another image.', { theme: 'light' });
      },
    }));
  };

  return (

    <Wrapper>
      <Text fontSize='24px' fontWeight={700}>Add Thumbnail</Text>
      {imageFileIsLoading || isThumbnailLoading ? (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      ) : (
        <DragHereContainer {...getRootProps()} isDragActive={isDragActive} image={image}>
          <input {...getInputProps()} />
          {image ? (
            <img
              src={image}
              alt='Preview'
              style={{
                width: '100%', borderRadius: '4px', objectFit: 'cover', height: '100%', objectPosition: 'center',
              }}
            />
          ) : (
            <>
              <IcomoonReact
                iconSet={iconSet}
                size={30}
                icon='gallery'
                color='#77A7FF'
              />
              <Text fontSize='16px' fontWeight={700} color='#77A7FF'>Click or drag image here</Text>
              <Text fontSize='13px' color='#9E9E9E'>Accepted file types: .jpg and .jpeg</Text>
            </>
          )}
        </DragHereContainer>
      )}
      <ButtonsContainer>
        <SaveButton onClick={() => (!imageFileIsLoading && !isThumbnailLoading && handleUpdateThumbnail())} backgroundColor={(imageFileIsLoading || isThumbnailLoading) ? '#BDBDBD' : '#444444'} disabled={imageFileIsLoading || isThumbnailLoading}>
          <Text color='white' fontSize='16px' fontWeight={700}>SAVE</Text>
        </SaveButton>
        <CancelButton onClick={() => closeModal()}>
          <Text fontSize='16px' fontWeight={700}>CANCEL</Text>
        </CancelButton>
      </ButtonsContainer>
    </Wrapper>

  );
};

export default AddThumbnailModalContent;
